import type { PropsWithChildren, ReactNode } from "react";
import { Modal, Pressable, StyleSheet, View } from "react-native";

import { Muted, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";

/**
 * App dialog: centered card over a scrim. Security-adjacent moments use
 * warm, factual copy — never accusatory (PRD tone rules).
 */
export function AppDialog({
  visible,
  title,
  body,
  leading,
  children,
  actions,
  onRequestClose
}: PropsWithChildren<{
  visible: boolean;
  title: string;
  body?: string;
  leading?: ReactNode;
  actions: ReactNode;
  onRequestClose: () => void;
}>) {
  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onRequestClose}>
      <Pressable
        accessibilityLabel="Dismiss dialog"
        onPress={onRequestClose}
        style={styles.scrim}
      >
        <Pressable style={styles.card} onPress={() => {}}>
          {leading}
          <Text variant="title">{title}</Text>
          {body ? <Muted style={styles.body}>{body}</Muted> : null}
          {children}
          <View style={styles.actions}>{actions}</View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  scrim: {
    alignItems: "center",
    backgroundColor: palette.scrim,
    flex: 1,
    justifyContent: "center",
    padding: space.xl
  },
  card: {
    backgroundColor: palette.surfaceRaised,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.xl,
    borderWidth: 1,
    gap: space.sm,
    maxWidth: 500,
    padding: space.xl,
    width: "100%"
  },
  body: {
    marginTop: space.xs
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: space.sm,
    justifyContent: "flex-end",
    marginTop: space.lg
  }
});
